import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import { toast } from '@/hooks/ui/use-toast';
import type {
  WhatsAppContact,
  WhatsAppContactWithDetails,
  WhatsAppMessage,
  UseWhatsAppContactsResult
} from '@/types/whatsapp';

export function useWhatsAppContacts(businessId: string | null): UseWhatsAppContactsResult {
  const queryClient = useQueryClient();
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  const queryKey = ['whatsapp-contacts', businessId];

  // Fetch contacts with last message and unread count
  const { data: contacts = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      // Get contacts
      const { data: contactsData, error: contactsError } = await supabase
        .from('whatsapp_contacts')
        .select('*')
        .eq('business_id', businessId)
        .order('last_inbound_at', { ascending: false, nullsFirst: false });

      if (contactsError) throw contactsError;

      // Get last message and unread count for each contact
      const contactsWithDetails = await Promise.all(
        (contactsData || []).map(async (contact: WhatsAppContact) => {
          // Get last message
          const { data: lastMessage } = await supabase
            .from('whatsapp_messages')
            .select('*')
            .eq('contact_id', contact.id)
            .order('sent_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          // Get unread count
          const { count: unreadCount } = await supabase
            .from('whatsapp_messages')
            .select('*', { count: 'exact', head: true })
            .eq('contact_id', contact.id)
            .eq('direction', 'inbound')
            .is('read_at', null);

          // Check if within 24h window
          const isWithinWindow = contact.last_inbound_at
            ? new Date(contact.last_inbound_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
            : false;

          return {
            ...contact,
            last_message: lastMessage as WhatsAppMessage | undefined,
            unread_count: unreadCount || 0,
            is_within_window: isWithinWindow,
          } as WhatsAppContactWithDetails;
        })
      );

      return contactsWithDetails;
    },
    enabled: !!businessId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Real-time subscription for contacts
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`whatsapp-contacts-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'whatsapp_contacts',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          // Refetch contacts on any change
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  // Real-time subscription for new messages (to update last_message and unread_count)
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`whatsapp-messages-contacts-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'whatsapp_messages',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          // Refetch contacts when new messages arrive
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  const selectedContact = contacts.find(c => c.id === selectedContactId) || null;

  const selectContact = useCallback((contactId: string | null) => {
    setSelectedContactId(contactId);
  }, []);

  const blockMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('whatsapp_contacts')
        .update({ is_blocked: true })
        .eq('id', contactId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Contact blocked');
    },
    onError: (error: Error) => {
      toast.error('Failed to block contact', { description: error.message });
    },
  });

  const unblockMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('whatsapp_contacts')
        .update({ is_blocked: false })
        .eq('id', contactId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Contact unblocked');
    },
    onError: (error: Error) => {
      toast.error('Failed to unblock contact', { description: error.message });
    },
  });

  const updateNotesMutation = useMutation({
    mutationFn: async ({ contactId, notes }: { contactId: string; notes: string }) => {
      const { error } = await supabase
        .from('whatsapp_contacts')
        .update({ notes })
        .eq('id', contactId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error('Failed to update notes', { description: error.message });
    },
  });

  return {
    contacts,
    isLoading,
    error: error as Error | null,
    selectedContact,
    selectContact,
    blockContact: (contactId) => blockMutation.mutateAsync(contactId),
    unblockContact: (contactId) => unblockMutation.mutateAsync(contactId),
    updateNotes: (contactId, notes) => updateNotesMutation.mutateAsync({ contactId, notes }),
    refresh: refetch,
  };
}
